/**
 * One-shot SFX playback for the hybrid preview.
 *
 * The music bed rides a continuous `<audio>` stem (see preview-session). Placed
 * one-shot SFX are different: each is a short sound fired at a single moment and
 * allowed to overlap. This scheduler decodes each placed asset into an
 * `AudioBuffer` once (cached) and, when the session tells it a sound's moment was
 * crossed during playback, plays a fresh `AudioBufferSourceNode` through a gain
 * node at the instance's volume. Multiple sources can play at once.
 *
 * Autoplay-safe: the `AudioContext` is created lazily and resumed on each fire
 * (fires only happen during user-initiated Play), mirroring the ui-thmp cue.
 * A no-AudioContext environment (jsdom/tests) degrades to a silent no-op.
 */

export interface SfxAssetSource {
  /** Public-relative primary path (Ogg Opus preferred). */
  path: string;
  /** Optional alternate (AAC/M4A) tried when the primary fails to decode. */
  fallbackPath?: string;
}

type AudioContextCtor = typeof AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

export class SfxScheduler {
  private ctx: AudioContext | null = null;
  private readonly buffers = new Map<string, AudioBuffer>();
  private readonly pending = new Set<string>();
  private disposed = false;

  /** @param resolveSource maps an asset id to its public paths (null if unknown). */
  constructor(private readonly resolveSource: (assetId: string) => SfxAssetSource | null) {}

  private ensureContext(): AudioContext | null {
    if (this.disposed) return null;
    if (!this.ctx) {
      const Ctor = audioContextCtor();
      if (!Ctor) return null;
      try {
        this.ctx = new Ctor();
      } catch {
        return null;
      }
    }
    if (this.ctx.state === "suspended") {
      void this.ctx.resume().catch(() => undefined);
    }
    return this.ctx;
  }

  /** Kick a decode for each asset id (idempotent + cached). Call on timeline updates. */
  prefetch(assetIds: readonly string[]): void {
    for (const id of assetIds) void this.load(id);
  }

  private async load(assetId: string): Promise<AudioBuffer | null> {
    const cached = this.buffers.get(assetId);
    if (cached) return cached;
    if (this.pending.has(assetId)) return null;
    const src = this.resolveSource(assetId);
    if (!src) return null;
    const ctx = this.ensureContext();
    if (!ctx || typeof fetch === "undefined") return null;
    this.pending.add(assetId);
    const urls = [src.path, src.fallbackPath]
      .filter((p): p is string => Boolean(p))
      .map((p) => (p.startsWith("/") ? p : `/${p}`));
    try {
      for (const url of urls) {
        try {
          const res = await fetch(url);
          if (!res.ok) continue;
          const arr = await res.arrayBuffer();
          const buf = await ctx.decodeAudioData(arr);
          if (!this.disposed) this.buffers.set(assetId, buf);
          return buf;
        } catch {
          /* try the next candidate url */
        }
      }
      return null;
    } finally {
      this.pending.delete(assetId);
    }
  }

  /**
   * Play a one-shot NOW at `volumeMilli` (0–1000), overlapping any others.
   * No-op (but kicks a decode) if the buffer is not ready yet, or if there is no
   * AudioContext. Never throws.
   */
  fire(assetId: string, volumeMilli: number): void {
    if (this.disposed) return;
    const ctx = this.ensureContext();
    if (!ctx) return;
    const buf = this.buffers.get(assetId);
    if (!buf) {
      void this.load(assetId);
      return;
    }
    try {
      const source = ctx.createBufferSource();
      source.buffer = buf;
      const gain = ctx.createGain();
      gain.gain.value = Math.max(0, Math.min(1, volumeMilli / 1000));
      source.connect(gain).connect(ctx.destination);
      source.start();
    } catch {
      /* node creation / start failed — ignore for a decorative one-shot */
    }
  }

  dispose(): void {
    this.disposed = true;
    this.buffers.clear();
    this.pending.clear();
    if (this.ctx) {
      void this.ctx.close?.().catch?.(() => undefined);
      this.ctx = null;
    }
  }
}
